import { Price } from '@deltager/organizers/admin/modules/events/create-event/model/price.model';
import { Attribute } from '@deltager/organizers/admin/modules/events/create-event/model/attribute.model';
import { EventLocation } from '@deltager/organizers/admin/modules/events/create-event/model/location.model';
import { BuiltinAttribute } from '@deltager/organizers/admin/modules/events/create-event/model/builtin-attributes.model';
import { VatType } from '@deltager/organizers/admin/modules/events/create-event/model/vat-type.enum';
import { EventConsent } from './event-consent.model';
import { OpenActiveAttendanceMode } from '@deltager/organizers/admin/modules/events/create-event/model/open-active-attendance-mode.enum';
import { EventSetupWizardStep } from '@deltager/organizers/admin/modules/events/create-event/model/event-setup-wizard-step.enum';
import { EventActiveMode } from './event-active-mode.enum';
import { Wizard } from './wizard.model';
import { WaitingListSettings } from '@deltager/organizers/admin/modules/events/create-event/model/waiting-list-settings.model';
import { Timespan } from '@deltager/organizers/admin/modules/events/create-event/model/timespan.model';
import {
  ConsentDisplayAs,
  PriceDropdownType,
} from '@deltager/organizers/admin/modules/events/create-event/create-event-step3/create-event-advanced-settings/create-event-advanced-settings.const';
import {
  FieldVisibility,
  FieldVisibilityOption,
  FieldVisibilityType,
} from '@deltager/organizers/admin/modules/events/create-event/model/field-visibility';
import { Image } from './image.model';
import { EventType } from '@deltager/organizers/admin/shared/models/EventType';
import _, { now } from 'lodash';
import { DayDate } from '@deltager/organizers/admin/core/date-format/dayDate';
import { Tag } from '../../../../shared/models/tag.model';
import { InstructorListItem } from '@deltager/organizers/admin/shared/models/instructor-list-item.model';
import { InvoiceSettings } from '@deltager/organizers/admin/modules/events/create-event/model/invoiceSettings';
import { NesSession } from '@deltager/organizers/admin/modules/event-sessions/event-session-calendar/session.model';
import { FileData } from '@deltager/organizers/admin/shared/models/FileData';
import { Municipality } from '@deltager/organizers/admin/shared/components/location/municipality.model';
import { Area } from '@deltager/organizers/admin/shared/components/location/area.model';
import { ExternalLoginSystem } from './external-login-system.model';
import { EventState } from '@deltager/organizers/admin/shared/enums/event-state.enum';

export class Event {
  id?: number;
  title?: string;
  images?: Image[];
  sessions?: NesSession[];
  registrationStartDate?: Date | string;
  registrationEndDate?: Date | string;
  startDate?: Date | string;
  endDate?: Date | string;
  contactPersonName?: string;
  contactPersonEmail?: string;
  contactPersonPhone?: string;
  bankAccountId?: number;
  payoutReference?: string;
  organizationNumber?: string;
  privatePersonPassportNumber?: string;
  registrationPageTemplateId?: number;
  description?: string;
  smsReceipt?: 0;
  generateBarCode?: 0;
  generateTicketBarcode?: 0;
  searchable?: 0;
  enableActivitiesSharing?: boolean;
  enableDiscountCodes?: boolean;
  checkoutPageEnabled?: boolean;
  discountCodeMandatory?: boolean;
  priceDropdownType: PriceDropdownType;
  location?: EventLocation;
  prices?: Price[];
  attributes?: Attribute[];
  associations?: { id: number }[];
  builtinAttributes?: BuiltinAttribute[];
  isShowImage?: boolean;
  maxRegistrations?: number;
  placesTaken: number;
  allowMaxNumberOfAvailablePlaces?: boolean;
  allowMaxTicketsPerOrder?: boolean;
  allowMultipleParticipants: boolean;
  maxParticipantsPerOrder?: number;
  maxTicketsPerOrder?: number;
  multiPriceCategoryTickets?: boolean;
  showPriceCategories: boolean;
  showPricesAs: boolean;
  showNumberOfAvailablePlaces?: boolean;
  confirmationEmailHeader: string;
  confirmationEmailFooter: string;
  emailConfirmation?: boolean;
  bccConfirmationEmailRecipients: string[];
  showSocialLinks?: boolean;
  enableShowInCalendar?: boolean;
  calendarDescription?: string;
  editable?: boolean;
  published?: boolean;
  isArchived: boolean;
  customRelativeUrl?: string;
  active?: EventActiveMode;
  state: EventState;
  registrationPageBaseUrl?: string;
  vatAvailability: VatType;
  totalRegistered: number;
  registered: number;
  payoutDelayDays?: number;

  startDateVisible: boolean;
  endDateVisible: boolean;
  registrationEndDateVisible: boolean;
  payable: boolean;
  eventType: EventType;
  consents?: EventConsent[];
  eventSetupStep: EventSetupWizardStep;

  openActivePublish: boolean;
  openActiveActivityId: string;
  openActiveActivityLabel: string;
  openActiveAttendanceMode: OpenActiveAttendanceMode;

  enableOnlinePayment: boolean;

  invoice: InvoiceSettings;

  useOrderApproval: boolean;
  orderApprovalDaysBeforeCancel: number;

  lockDescription: boolean;
  lockEventName: boolean;
  waitingListEnabled: boolean;
  waitingListSettings: WaitingListSettings;
  numberOfWaitingListRegistrations: number;

  organizerId: number;
  isPriceBreakDownEnabled: boolean;
  isPendingOnboarding: boolean;

  accountingSystemProjectCode: string;
  accountingSystemDepartmentCode: string;
  fieldVisibility: FieldVisibilityOption[];
  language: number;
  parentId: number;
  tags: Tag[];
  typeId: number;
  levelId: number;
  externalUrl?: string;
  isExternalPublish: boolean;
  useSessions: boolean;
  userProfileLoginToolEnabled: boolean;
  guestUserRegistrationEnabled: boolean;
  consentDisplayAs: ConsentDisplayAs;
  cancellationPolicyId?: number;
  instructors: InstructorListItem[];
  isRiskAssessed?: boolean;
  riskAssessmentUrl?: string;
  isDiplomasCertificateTagsEnabled: boolean;
  certificateDescription?: string;
  municipalities?: Municipality[];
  areas?: Area[];

  documentUrls?: string[];
  documents?: FileData[];

  wereRegistrations: boolean;

  useExternalLogin: boolean;
  isPurchaseOrderNumberMandatory: boolean;
  externalLoginSystem: ExternalLoginSystem;
  isCancelled: boolean;
  isRefunded: boolean;
  surveyTemplateId?: number;
  sendOrderConfirmationToAllParticipants: boolean;

  constructor(initValue?: Partial<Event>) {
    Object.assign(this, initValue);
  }

  shouldHideField(type: FieldVisibilityType): boolean {
    return this.fieldVisibility?.find(option => option.type === type)?.visibility === FieldVisibility.Hide;
  }

  validate(): boolean {
    //Dates
    if (this.startDate <= this.endDate) return false;

    //Implement all other validations
  }

  get hasVat(): boolean {
    return this.vatAvailability !== VatType.NO_VAT;
  }

  private static setEndTimeBasedOnStartTime(): Operation {
    return new Operation((initialEvent, currentEvent) => {
      if (initialEvent.startDateIsSet) {
        currentEvent.endDate = DayDate.addHours(new Date(currentEvent.startDate), 1);
      }

      return currentEvent;
    });
  }

  private static setDefaultRegistrationDates(): Operation {
    return new Operation((initialEvent, currentEvent) => {
      if (initialEvent.startDateIsSet) {
        currentEvent.registrationStartDate = new Date(new Date(new Date(now()).setHours(0)).setMinutes(0));
        currentEvent.registrationEndDate = DayDate.endOfDay(DayDate.addDays(new Date(currentEvent.startDate), -1));
      }

      return currentEvent;
    });
  }

  private static setRegistrationEndDateAsStartDate(): Operation {
    return new Operation((initialEvent, currentEvent) => {
      if (
        initialEvent.startDateIsSet &&
        (initialEvent.startDate as Date).getDate() !== (currentEvent.registrationStartDate as Date).getDate()
      ) {
        currentEvent.registrationEndDate = DayDate.endOfDay(DayDate.addDays(new Date(currentEvent.startDate), -1));
      }

      return currentEvent;
    });
  }

  private static setEndTime(): Operation {
    return new Operation((initialEvent, currentEvent) => {
      if (initialEvent.startTimeIsSet && initialEvent.endTimeInInitialState) {
        currentEvent.endDate = DayDate.addHours(new Date(currentEvent.startDate), 1);
      }

      return currentEvent;
    });
  }

  private static setRegistrationEndTime(): Operation {
    return new Operation((initialEvent, currentEvent) => {
      if (initialEvent.startTimeIsSet && initialEvent.registrationEndTimeInInitialState) {
        currentEvent.registrationEndDate = DayDate.endOfDay(DayDate.addDays(new Date(currentEvent.startDate), -1));
      }

      return currentEvent;
    });
  }

  prePopulateEventDates(): Event {
    const setEventDatesPipeline = Event.setEndTimeBasedOnStartTime();
    setEventDatesPipeline.setNextOperation(Event.setEndTime());

    return setEventDatesPipeline.handle(new Event(this), this);
  }

  prePopulateRegistrationEventDates(): Event {
    const setRegistrationEventDatesPipeline = Event.setEndTimeBasedOnStartTime();
    setRegistrationEventDatesPipeline
      .setNextOperation(Event.setEndTime())
      .setNextOperation(Event.setDefaultRegistrationDates())
      .setNextOperation(Event.setRegistrationEndDateAsStartDate())
      .setNextOperation(Event.setRegistrationEndTime());

    return setRegistrationEventDatesPipeline.handle(new Event(this), this);
  }

  static BuildEventModel(
    contactPersonName: string,
    contactPersonPhone: string,
    contactPersonEmail: string,
    orgNumber: string,
    personNumber: string,
    templateId: number,
    wizard: Wizard,
    eventType: EventType = EventType.Regular,
  ): Event {
    return new Event({
      active: EventActiveMode.Incomplete,
      prices: [],
      startDate: undefined,
      organizationNumber: orgNumber,
      privatePersonPassportNumber: personNumber,
      endDate: undefined,
      registrationStartDate: undefined,
      registrationEndDate: undefined,
      attributes: [],
      builtinAttributes: [],
      isShowImage: false,
      location: new EventLocation(),
      contactPersonName: contactPersonName,
      contactPersonPhone: contactPersonPhone,
      contactPersonEmail: contactPersonEmail,
      startDateVisible: true,
      endDateVisible: true,
      registrationEndDateVisible: true,
      payable: true,
      images: [],
      eventType: eventType,
      registrationPageTemplateId: templateId,
      vatAvailability: VatType.NO_VAT,
      lockDescription: wizard?.lockDescription ?? false,
      lockEventName: wizard?.lockEventName ?? false,
      title: wizard?.eventName ?? '',
      description: wizard?.description ?? '',
    });
  }

  public adjustEventBeforeSave(initialEvent: Event) {
    const resultEvent = Object.assign(new Event(), this);

    resultEvent.prices = this.adjustPricesBeforeSave(resultEvent);

    resultEvent.attributes = this.adjustAttributesBeforeSave(resultEvent, initialEvent);

    resultEvent.builtinAttributes = _.cloneDeep(this.builtinAttributes).map(bAttr => {
      bAttr.uiId = undefined;

      if (bAttr.name.length > 0 && bAttr.name === bAttr.wizardAttributeName) {
        bAttr.name = '';
      }

      return bAttr;
    });

    delete resultEvent.isShowImage;
    delete resultEvent.allowMaxNumberOfAvailablePlaces;
    delete resultEvent.editable;

    resultEvent.startDate = (resultEvent.startDate as Date)?.toISOString() ?? initialEvent.startDate;
    resultEvent.endDate = (resultEvent.endDate as Date)?.toISOString() ?? initialEvent.endDate;

    resultEvent.location ??= { ...initialEvent.location };

    resultEvent.invoice = _.cloneDeep(resultEvent.invoice);
    resultEvent.invoice.scheduledSendDate = !!resultEvent.invoice.scheduledSendDate
      ? DayDate.toIsoWithoutTimezone(resultEvent.invoice.scheduledSendDate)
      : null;

    resultEvent.registrationStartDate = !!resultEvent.registrationStartDate
      ? (resultEvent.registrationStartDate as Date).toISOString()
      : null;
    resultEvent.registrationEndDate = !!resultEvent.registrationEndDate
      ? (resultEvent.registrationEndDate as Date).toISOString()
      : null;

    if (resultEvent.waitingListSettings) {
      resultEvent.waitingListSettings = _.cloneDeep(resultEvent.waitingListSettings);

      if (typeof resultEvent.waitingListSettings.invitationExpiry === 'string') {
        resultEvent.waitingListSettings.invitationExpiry = Timespan.parse(
          resultEvent.waitingListSettings.invitationExpiry as string,
        ).format();
      } else if (resultEvent.waitingListSettings.invitationExpiry instanceof Timespan) {
        resultEvent.waitingListSettings.invitationExpiry = (
          resultEvent.waitingListSettings.invitationExpiry as Timespan
        ).format();
      }
    }

    return resultEvent;
  }

  public buildFullRegistrationPageUrl(newUrl: string | null = null, useSsoFriendlyUrl: boolean) {
    if (this.customRelativeUrl || newUrl) {
      const urlPart = useSsoFriendlyUrl ? '/' : '/#/';
      return `${this.registrationPageBaseUrl.replace(/\/+$/, '')}${urlPart}${newUrl ?? this.customRelativeUrl}`;
    } else {
      return this.buildFullRegistrationPageUrlDefault(useSsoFriendlyUrl);
    }
  }

  public buildFullRegistrationPageUrlDefault(useSsoFriendlyUrl: boolean) {
    const urlPart = useSsoFriendlyUrl ? '/' : '/#/';
    return `${this.registrationPageBaseUrl?.replace(/\/+$/, '')}${urlPart}${this.id}`;
  }

  get startDateIsSet(): boolean {
    return !!this.startDate && !this.endDate;
  }

  get timeInInitialState() {
    return this.endDate && this.startTimeInInitialState && this.timeInInitialStateExceptStartDate;
  }

  get endTimeInInitialState(): boolean {
    return this.timeInitialState(this.endDate as Date);
  }

  get startTimeInInitialState(): boolean {
    return this.timeInitialState(this.startDate as Date);
  }

  get registrationEndTimeInInitialState(): boolean {
    return this.timeInitialState(this.registrationEndDate as Date);
  }

  private get startTimeIsSet(): boolean {
    return !!this.startDate && ((this.startDate as Date).getHours() != 0 || (this.startDate as Date).getMinutes() != 0);
  }

  private timeInitialState(time: Date): boolean {
    return !!time && time.getHours() === 0 && time.getMinutes() === 0;
  }

  get timeInInitialStateExceptStartDate(): boolean {
    return this.startDate && this.endTimeInInitialState && this.registrationEndTimeInInitialState;
  }

  get registrationDatesAreSet(): boolean {
    return !!this.registrationStartDate || !!this.registrationEndDate;
  }

  get isNew(): boolean {
    return this.id > 0 && !this.published;
  }

  get hasPriceComponentAvailability(): boolean {
    if (this.prices) {
      return this.prices.some(p => p.isBreakDownPrice);
    }

    return false;
  }

  private adjustPricesBeforeSave(resultEvent: Event): Price[] {
    return _.cloneDeep(this.prices).map(p => {
      if (p === undefined) {
        return undefined;
      }
      const resultPrices = Object.assign({}, p);
      resultPrices.uiId = undefined;
      resultPrices.hasAttributeDependencies = undefined;
      resultPrices.ageLimit = undefined;
      resultPrices.ageLimitMin = p.ageLimitMin > 0 ? p.ageLimitMin : 0;
      resultPrices.ageLimitMax = p.ageLimitMax > 0 ? p.ageLimitMax : 0;

      if (resultPrices.ageLimitDateFrom && new Date(resultPrices.ageLimitDateFrom)) {
        const parsedAgeLimitDateFrom = new Date(resultPrices.ageLimitDateFrom);

        resultPrices.ageLimitDateFrom = new Date(
          Date.UTC(
            parsedAgeLimitDateFrom.getFullYear(),
            parsedAgeLimitDateFrom.getMonth(),
            parsedAgeLimitDateFrom.getDate(),
          ),
        ).toISOString();
      }

      if (!!resultPrices.ageLimitDateTo && new Date(resultPrices.ageLimitDateTo)) {
        const parsedAgeLimitDateTo = new Date(resultPrices.ageLimitDateTo);

        resultPrices.ageLimitDateTo = new Date(
          Date.UTC(parsedAgeLimitDateTo.getFullYear(), parsedAgeLimitDateTo.getMonth(), parsedAgeLimitDateTo.getDate()),
        ).toISOString();
      }

      if (!resultEvent.hasVat) {
        resultPrices.vat = 0;
      }

      return resultPrices;
    });
  }

  private adjustAttributesBeforeSave(resultEvent: Event, initialEvent: Event) {
    return _.cloneDeep(this.attributes).map(attr => {
      if (attr === undefined) {
        return undefined;
      }

      const initAttr = initialEvent.attributes.find(a => a.id === attr.id);

      const resultAttr = Object.assign({}, attr) as Attribute;
      resultAttr.uiId = undefined;

      if (!!initAttr) {
        resultAttr['wizardId'] = initAttr['wizardId'] ?? 0;
      }

      delete resultAttr.isShowAttrType;

      if (!resultEvent.hasVat) {
        resultAttr.vat = 0;
      }

      resultAttr.options
        ?.filter(item => item !== undefined)
        .map(item => {
          if (item.uiId) {
            item.uiId = undefined;
          }

          delete item.hasAttributeDependencies;

          return item;
        });

      resultAttr.dependencies
        .filter(item => item !== undefined)
        .map(item => {
          if (item.uiId) {
            item.uiId = undefined;
          }
          if (item.dependencyType === null) {
            item.dependencyType = undefined;
          }
          if (item.relatedList) {
            item.relatedList = undefined;
          }
          if (item.currentDependencyList) {
            item.currentDependencyList = undefined;
          }
          return item;
        });
      return resultAttr;
    });
  }
}

class Operation {
  private readonly handler: (initialEvent: Event, currentEvent: Event) => Event;
  next?: Operation;

  constructor(handler: (initialEvent: Event, currentEvent: Event) => Event) {
    this.handler = handler;
  }

  setNextOperation(operation: Operation): Operation {
    this.next = operation;

    return this.next;
  }

  handle(initialEvent: Event, currentEvent: Event): Event {
    const event: Event = this.handler(initialEvent, currentEvent);

    if (this.next) {
      return this.next.handle(initialEvent, event);
    }

    return event;
  }
}
